import { api } from '../api';

/**
 * A programação de ensaios especiais por MÁQUINA — os caminhos.
 *
 * «O ideal do cronograma é ser por equipamento, separado em
 *  Adensamentos, Triaxiais, Cisalhamento direto» — Testesolo,
 * 02/09/2026. E, sobre o que a barra significa: «ocupação do
 * equipamento».
 *
 * 0189: o programado e o realizado são duas camadas, o programado tem
 * revisões numeradas, e há uma porta só (`programar`) que grava data,
 * máquina e duração na mesma transação.
 */

export interface Maquina {
  equipamento_id: string;
  codigo_patrimonial: string;
  nome: string;
  categoria: string;
  categoria_nome: string;
  categoria_ordem: number;
  postos: number;
  status: string;
  localizacao: string | null;
  apto: boolean | null;
  calibracao_ate: string | null;
  impedimento: string | null;
  ensaios_na_frente: number;
  /** `null` quando a máquina está impedida: ela não fica livre. */
  livre_em: string | null;
  /** 0191: grupos de método que ESTA máquina aceita além da categoria. */
  grupos_extras: string[];
  /**
   * 0195 (RN-196): o dia em que um ensaio arrastado para cá começa — o
   * próximo dia útil do laboratório a partir de `livre_em`, nunca hoje.
   * `null` quando a máquina está impedida.
   */
  proximo_inicio: string | null;
}

/** 0195: um feriado da bancada dentro da janela. */
export interface FeriadoNaJanela { dia: string; nome: string | null; }

/**
 * 0197: uma FRENTE de serviço — a linha dos ensaios triviais no mesmo
 * Gantt, abaixo das máquinas. A frente não tem posto: tem capacidade em
 * ensaios por dia, e `carga` diz quantos ensaios ocupam cada dia (só os
 * dias com algum ensaio, dentro da janela e 30 dias depois dela).
 */
export interface Frente {
  id: string;
  codigo: string;
  nome: string;
  familia: 'caracterizacao' | 'compactacao';
  ordem: number;
  capacidade_dia: number;
  ensaios_na_frente: number;
  carga: { dia: string; n: number }[];
  /** Os métodos que esta frente executa (frente_metodo). */
  metodos: string[] | null;
}

export const gravarCapacidade = (frenteId: string, capacidadeDia: number) =>
  api<{ id: string; codigo: string; capacidade_dia: number }>(
    `/cronograma/maquinas/frentes/${frenteId}/capacidade`,
    { metodo: 'PATCH', corpo: { capacidadeDia } });

/** 0191: a duração padrão de um método especial. */
export interface Duracao {
  id: string;
  codigo: string;
  nome: string;
  grupo: string | null;
  duracao_padrao_dias: string | null;
  duracao_prevista: number;
  estagios: number;
}

export const verDuracoes = () => api<Duracao[]>('/cronograma/maquinas/duracoes');
export const gravarDuracao = (metodoId: string, dias: number) =>
  api<{ id: string; codigo: string; duracao_padrao_dias: string; duracao_prevista: number }>(
    `/cronograma/maquinas/duracoes/${metodoId}`, { metodo: 'PATCH', corpo: { dias } });

export interface Barra {
  /** Onda 200: o escopo do método (0083) — a cor da barra. */
  escopo?: string | null;
  ensaio_id: string;
  equipamento_id: string | null;
  equipamento_codigo: string | null;
  equipamento_nome: string | null;
  categoria: string | null;
  os_id: string;
  os_numero: string | null;
  obra_nome: string | null;
  cliente: string | null;
  amostra_id: string;
  amostra_codigo: string;
  identificacao_recebida: string | null;
  tipo_amostra: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  metodo_id: string;
  metodo_codigo: string;
  metodo_nome: string;
  situacao: string;
  inicio_previsto: string;
  duracao_dias: string | number;
  duracao_do_metodo: boolean;
  corre_no_fim_de_semana: boolean;
  fim_previsto: string | null;
  /** O primeiro dado bruto lançado (0086). */
  inicio_real: string | null;
  /** 0189: a FICHA COMPLETA (dados_completos) — não a aprovação. */
  fim_real: string | null;
  executor: string | null;
  observacoes: string | null;
  programado_por_nome: string | null;
  programado_em: string | null;
  percentual: number;
  atrasado: boolean;
  em_conflito?: boolean;
  simultaneos?: number;
  postos?: number;
  /* ── 0169 ── */
  prazo_contratual: string | null;
  cliente_id: string | null;
  /** O PRIMEIRO previsto, quando a programação nasceu (referência inicial). */
  inicio_base: string | null;
  duracao_base: string | number | null;
  fim_base: string | null;
  /** A barra saiu de onde a entrega prometeu. */
  remanejado: boolean;
  /** De quem este ensaio depende — derivado do catálogo, por amostra. */
  depende_de: string[] | null;
  fim_tardio: string | null;
  /** Dias que pode atrasar sem empurrar a entrega. NULL = OS sem prazo. */
  folga_dias: number | null;
  critico: boolean | null;
  /* ── 0189 ── */
  /** De onde saiu o fim real: `ficha_completa`, ou `marco_posterior` para
      ensaio anterior à 0118 (o primeiro marco após a execução). */
  fim_real_origem: 'ficha_completa' | 'marco_posterior' | null;
  /** Quantas vezes a ficha foi reaberta depois de devolvida. */
  reaberturas: number | null;
  /** A última vez que a ficha ficou completa, se diferente da primeira. */
  fim_real_corrigido: string | null;
  /** Começou e ainda não terminou: a barra real vai até hoje. */
  em_execucao_aberta: boolean;
  /** A duração programada contada do início REAL — é previsão. */
  previsao_fim: string | null;
  /** Concluídos: fim real − fim programado, em dias. */
  desvio_termino_dias: number | null;
  /** Não concluídos: dias passados do fim programado. 0 = no prazo. */
  atraso_atual_dias: number;
  duracao_unidade: string;
  /** Número da revisão vigente do programado (0 = nunca revisado). */
  revisao_atual: number;
  duracao_do_metodo_dias: string | number | null;
  /* ── 0197 ── */
  /** A frente de serviço (ensaio trivial). Exclusiva com a máquina. */
  frente_id: string | null;
  frente_codigo: string | null;
  frente_nome: string | null;
  /* ── 0198 ── */
  /** O grupo do método e as categorias de máquina que o aceitam — para a
      barra arrastada para OUTRA máquina passar pela mesma checagem do chip. */
  grupo: string | null;
  categorias_aceitas: string[] | null;
}

/** 0198: um movimento da proposta de resolução de conflitos. */
export interface Movimento {
  ensaio_id: string;
  equipamento_id: string;
  equipamento_codigo: string;
  metodo_codigo: string;
  amostra_codigo: string;
  /** Onda 303 (RN-248): o nome que o cliente deu à amostra. */
  identificacao_recebida?: string | null;
  os_numero: string | null;
  folga_dias: number | null;
  critico: boolean;
  inicio_atual: string;
  fim_atual: string;
  inicio_novo: string;
  fim_novo: string;
  ordem: number;
  dias_adiados: number;
}

export interface PropostaConflitos {
  movimentos: Movimento[];
  antes: { ensaios: number; maquinas: number; fixos: number };
}

/** A proposta — não grava. Escopo: nada (tudo), uma máquina ou um ensaio. */
export const proporConflitos = (escopo: { equipamentoId?: string; ensaioId?: string }) =>
  api<PropostaConflitos>('/cronograma/maquinas/conflitos/propor', { metodo: 'POST', corpo: escopo });

/** Grava os movimentos confirmados, numa transação; 409 se algo mudou. */
export const aplicarConflitos = (corpo: {
  movimentos: { ensaioId: string; inicioAtual: string; inicioNovo: string }[];
  motivo?: string;
}) => api<{ movidos: number; feitos: { ensaioId: string; inicioAnterior: string; inicioNovo: string }[]; conflitos_restantes: number }>(
  '/cronograma/maquinas/conflitos/aplicar', { metodo: 'POST', corpo });

export interface Revisao {
  id: string;
  ensaio_id: string;
  numero: number;
  inicio_anterior: string | null;
  inicio_novo: string | null;
  duracao_anterior: string | number | null;
  duracao_nova: string | number | null;
  corre_fds_anterior: boolean | null;
  corre_fds_novo: boolean | null;
  equipamento_anterior: string | null;
  equipamento_anterior_codigo: string | null;
  equipamento_novo: string | null;
  equipamento_novo_codigo: string | null;
  fim_novo: string | null;
  origem: 'formulario' | 'gantt' | 'lote' | 'grade' | 'sistema';
  motivo: string | null;
  usuario_id: string | null;
  usuario_nome: string | null;
  datahora: string;
}

export interface Contadores {
  sem_agendamento: number;
  agendados: number;
  em_execucao: number;
  concluidos: number;
  atrasados: number;
  unidade: 'ensaios';
}

export interface OsNoFiltro {
  id: string; numero: string | null; cliente: string | null;
  prazo_contratual: string | null;
}
export interface ClienteNoFiltro { id: string; nome: string; }

export interface NaBandeja {
  ensaio_id: string;
  os_id: string;
  os_numero: string | null;
  obra_nome: string | null;
  cliente: string | null;
  amostra_id: string;
  amostra_codigo: string;
  identificacao_recebida: string | null;
  profundidade_inicial: string | null;
  profundidade_final: string | null;
  metodo_id: string;
  metodo_codigo: string;
  metodo_nome: string;
  situacao: string;
  data_prevista: string | null;
  duracao_sugerida: number;
  /** Onda 200: o escopo do método (0083) — a cor do chip. */
  escopo: string | null;
  cliente_id: string | null;
  grupo: string | null;
  /** `null` = o grupo ainda não tem categoria de máquina declarada. */
  categorias_aceitas: string[] | null;
  /* ── 0197 ── */
  classe: 'especial' | 'trivial';
  /** A frente que o método declara (frente_metodo); trivial sem frente = qualquer. */
  frente_sugerida_id: string | null;
  frente_sugerida_codigo: string | null;
}

export interface MetodoEspecial {
  id: string; codigo: string; nome: string; grupo: string | null;
  /** 0197: a lista traz as duas classes. */
  classe: 'especial' | 'trivial';
}

export interface Resposta {
  maquinas: Maquina[]; barras: Barra[]; bandeja: NaBandeja[];
  /** 0197: as frentes de serviço, abaixo das máquinas. */
  frentes: Frente[];
  metodos: MetodoEspecial[]; oss: OsNoFiltro[]; clientes: ClienteNoFiltro[];
  janela: number;
  /** O «hoje» da bancada (fn_hoje), não o do navegador. */
  hoje: string | null;
  /** 0198: o «a partir de» desta resposta (AAAA-MM-DD). */
  de: string | null;
  /** 0195: os feriados da bancada na janela (e 30 dias depois dela). */
  feriados: FeriadoNaJanela[];
  contadores: Contadores;
}

export const verMaquinas = (f: {
  de?: string; dias?: number; equipamento?: string; metodo?: string;
  busca?: string; os?: string; cliente?: string; so_critico?: boolean;
  /** 0197: `especial` | `trivial` | vazio (todos). */
  classe?: string;
} = {}) => {
  const q = new URLSearchParams();
  Object.entries(f).forEach(([k, v]) => { if (v) q.set(k, String(v)); });
  const s = q.toString();
  return api<Resposta>(`/cronograma/maquinas${s ? `?${s}` : ''}`);
};

export const verRevisoes = (ensaioId: string) =>
  api<Revisao[]>(`/cronograma/maquinas/ensaio/${ensaioId}/revisoes`);

/**
 * A conta do calendário, feita pelo banco (feriados da bancada, 0168):
 * início + duração → fim, ou início + fim → duração.
 */
export const contaDatas = (f: {
  inicio: string; fim?: string; duracao?: number; fds?: boolean;
}) => {
  const q = new URLSearchParams();
  q.set('inicio', f.inicio);
  if (f.fim) q.set('fim', f.fim);
  if (f.duracao != null) q.set('duracao', String(f.duracao));
  if (f.fds) q.set('fds', '1');
  return api<{ fim: string | null; duracao: number | null }>(
    `/cronograma/maquinas/conta?${q.toString()}`);
};

/**
 * Programar — data, máquina, duração e calendário numa transação só
 * (0189). As três relações entre os campos, resolvidas no servidor:
 * veio `inicio` → mantém a duração e recalcula o fim; veio `fim` →
 * mantém o início e recalcula a duração; veio `duracaoDias` → mantém o
 * início e recalcula o fim. `motivo` é obrigatório quando a data de um
 * ensaio já programado muda.
 */
export const programar = (corpo: {
  ensaioId: string;
  inicio?: string;
  fim?: string;
  duracaoDias?: number | null;
  equipamentoId?: string | null;
  /** 0197: a frente (ensaio trivial). Informar limpa a máquina, e vice-versa. */
  frenteId?: string | null;
  correNoFimDeSemana?: boolean;
  observacoes?: string;
  motivo?: string;
  origem: 'formulario' | 'gantt' | 'lote';
}) => api<{ barra: Barra; mudouData: boolean }>(
  '/cronograma/maquinas/programar', { metodo: 'POST', corpo });

/**
 * Alocar, mover de máquina, mudar a duração — ou devolver à bandeja.
 *
 * `equipamentoId: null` desaloca. `duracaoDias: null` volta para a
 * duração que o método declara. A DATA não passa por aqui: arrastar no
 * eixo do tempo é `programar`, que grava os dois juntos.
 */
export const alocar = (corpo: {
  ensaioId: string;
  equipamentoId?: string | null;
  duracaoDias?: number | null;
  correNoFimDeSemana?: boolean;
  observacoes?: string;
  motivo?: string;
  origem?: 'formulario' | 'gantt' | 'lote';
}) => api<Barra | { desalocado: boolean }>('/cronograma/maquinas/alocar',
  { metodo: 'POST', corpo });

export const definirPostos = (equipamentoId: string, postos: number) =>
  api<{ id: string; codigo_patrimonial: string; postos: number }>(
    `/cronograma/maquinas/${equipamentoId}/postos`,
    { metodo: 'POST', corpo: { postos } });
